<template>
  <div
    ref="cardRoot"
    class="bank-card-container mb-4 bank-view-root"
    :style="cardBgStyle"
    :class="[
      textColor === 'dark' ? 'text-mode-dark' : 'text-mode-white',
      hasImageBg ? 'is-image-bg' : '' 
    ]"
  >  
    <!-- هاله شیشه‌ای نور فقط در صورت عدم وجود عکس -->
    <div v-if="!hasImageBg" class="card-glass-glow"></div>

    <!-- بدنه داخلی کارت -->
    <div class="card-inner-layout">
      
      <!-- ۱. هدر کارت: فقط در حالت بدون عکس فعال است -->
      <div v-if="!hasImageBg" class="d-flex justify-space-between align-center px-4 pt-3">
        <div class="d-flex align-center">
          <v-img 
            v-if="bankLogo"
            :src="bankLogo"
            max-width="28"
            max-height="28"
            contain
            class="me-2 rounded-circle"
          />
          <span class="bank-title font-weight-bold">{{ bankName }}</span>
        </div>

        <div class="emv-chip">
          <div class="chip-line"></div>
        </div>
      </div>

      <!-- اگر تصویر پس‌زمینه دارد، فاصله خالی در بالا تا شماره کارت دقیقاً زیر چیپ بیفتد -->
      <div v-else class="card-image-top-spacer"></div>

      <!-- ۲. شماره کارت -->
      <div class="px-4 d-flex justify-space-between align-center my-auto">
        <div class="card-number ltr-text font-weight-bold">
          {{ formattedCardNumber }}
        </div>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              small
              class="copy-btn"
              v-bind="attrs"
              v-on="on"
              @click.stop="copyText(rawCardNumber, 'شماره کارت')"
            >
              <v-icon small :color="iconColor">mdi-content-copy</v-icon>
            </v-btn>
          </template>
          <span>کپی شماره کارت</span>
        </v-tooltip>
      </div>

      <!-- ۳. نام صاحب کارت -->
      <div class="px-4 d-flex justify-space-between align-center pb-1">
        <div class="holder-name text-truncate">
          {{ ownerName }}
        </div>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              x-small
              class="copy-btn"
              v-bind="attrs"
              v-on="on"
              @click.stop="copyText(ownerName, 'نام صاحب حساب')"
            >
              <v-icon x-small :color="iconColor">mdi-content-copy</v-icon>
            </v-btn>
          </template>
          <span>کپی نام صاحب حساب</span>
        </v-tooltip>
      </div>

      <!-- ۴. بخش شماره شبا و حساب -->
      <div class="card-footer px-4 py-2 mt-auto dirltr">
        <!-- شبا -->
        <div v-if="ibanNumber" class="d-flex justify-space-between align-center mb-1">
          <div class="info-tag ltr-text">
            <span class="label">IR:</span> {{ cleanIban(ibanNumber) }}
          </div>
          <v-btn
            icon
            x-small
            class="copy-btn"
            @click.stop="copyText(formatIban(ibanNumber), 'شماره شبا')"
          >
            <v-icon x-small :color="iconColor">mdi-content-copy</v-icon>
          </v-btn>
        </div>

        <!-- حساب -->
        <div v-if="accountNumber" class="d-flex justify-space-between align-center">
          <div class="info-tag ltr-text">
            <span class="label">حساب:</span> {{ accountNumber }}
          </div>
          <v-btn
            icon
            x-small
            class="copy-btn"
            @click.stop="copyText(accountNumber, 'شماره حساب')"
          >
            <v-icon x-small :color="iconColor">mdi-content-copy</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- توضیحات کارت در صورت وجود -->
      <div 
        v-if="card.description" 
        class="card-description-bar px-4 py-1 d-flex align-center cursor-pointer"
        @click="showDescDialog = true"
      >
        <v-icon x-small :color="iconColor" class="me-1">mdi-text-box-outline</v-icon>
        <span class="text-caption text-truncate">{{ card.description }}</span>
      </div>
    </div>

    <!-- مودال توضیحات -->
    <v-dialog
      v-model="showDescDialog"
      max-width="320"
      :attach="$refs.cardRoot"
      :retain-focus="false" 
      overlay-color="#000"
      overlay-opacity="0.35"
      content-class="glass-dialog-content"
    >
      <v-card class="glass-card rounded-xl pa-3">
        <div class="d-flex justify-space-between align-center pb-2">
          <div class="d-flex align-center font-weight-bold text-subtitle-2 text-primary-dark">
            <v-icon color="primary" class="me-1" small>mdi-information</v-icon>
            <span>توضیحات {{ bankName }}</span>
          </div>
          <v-btn icon x-small class="close-icon-btn" @click="showDescDialog = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-divider class="glass-divider mb-2"></v-divider>
        <div class="desc-content px-1 py-1 text-body-2">
          {{ card.description }}
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: "BankCardItem",
  props: {
    card: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    defaultBg: {
      type: String,
      default: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)",
    },
    textColor: {
      type: String,
      default: "white",
    },
    hasImageBg: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      showDescDialog: false,
    };
  },
  computed: {
    bankName() {
      return this.card.bank_name || this.card.bankName || "بانک";
    },
    bankLogo() {
      return this.card.bank_logo || this.card.bankLogo || null;
    },
    rawCardNumber() {
      return (this.card.cart_number || this.card.cardNumber || "").toString();
    },
    formattedCardNumber() {
      if (!this.rawCardNumber) return "---- ---- ---- ----";
      const cleaned = this.rawCardNumber.replace(/\D/g, "");
      const matches = cleaned.match(/.{1,4}/g);
      return matches ? matches.join("  ") : this.rawCardNumber;
    },
    ownerName() {
      return this.card.owner_name || this.card.ownerName || "نام صاحب حساب";
    },
    ibanNumber() {
      return this.card.shaba_number || this.card.iban || "";
    },
    accountNumber() {
      return this.card.account_number || this.card.accountNumber || "";
    },
    iconColor() {
      return this.textColor === "dark" ? "#1f2937" : "#ffffff";
    },
    cardBgStyle() {
      const bg = this.defaultBg || this.card.bank_color || "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)";
      return {
        background: bg,
        backgroundSize: "100% 100%",
        backgroundPosition: "center center",
        backgroundRepeat: "no-repeat"
      };
    },
  },
  methods: {
    cleanIban(iban) {
      if (!iban) return "";
      return iban.toString().replace(/^IR/i, "");
    },
    formatIban(iban) {
      if (!iban) return "";
      return `IR${this.cleanIban(iban)}`;
    },
    copyText(text, title) {
      if (!text) return;
      navigator.clipboard
        .writeText(text)
        .then(() => {
          if (this.$toast) this.$toast.success(`${title} کپی شد`);
        })
        .catch(() => {
          if (this.$toast) this.$toast.error("خطا در کپی");
        });
    },
  },
};
</script>

<style scoped>
.bank-card-container {
  overflow: visible !important;
  margin-bottom: 40px !important;
  width: 100%;
  max-width: 420px;
  aspect-ratio: 1.586 / 1; 
  border-radius: 16px;
  position: relative !important;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.22);
  font-family: "Vazirmatn", sans-serif;
  transition: transform 0.2s ease;
  box-sizing: border-box;
}

.card-inner-layout {
  position: relative;
  z-index: 2;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* فاصله خالی برای رد کردن لوگو و چیپ عکس اصلی */
.card-image-top-spacer {
  height: 44%;
  width: 100%;
  flex-shrink: 0;
}

.bank-title {
  font-size: 0.92rem !important;
}

.card-number {
  font-size: 1.28rem !important;
  letter-spacing: 2px !important;
}

.holder-name {
  font-size: 0.95rem !important;
  font-weight: 600 !important;
}

.card-footer {
  font-size: 0.82rem !important;
}

.card-footer .info-tag {
  font-size: 0.82rem !important;
  font-weight: 500;
}

.info-tag .label {
  margin-right: 4px;
  padding: 1px 4px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.18);
}

/* حالت متن سفید */
.text-mode-white {
  color: #ffffff;
}
.text-mode-white .card-number {
  text-shadow: 0 2px 6px rgba(0, 0, 0, 0.85);
}
.text-mode-white .holder-name {
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.7);
}
.text-mode-white .card-footer {
  background: rgba(0, 0, 0, 0.38);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-top: 1px solid rgba(255, 255, 255, 0.12);
}
.text-mode-white .copy-btn {
  background: rgba(255, 255, 255, 0.2);
}

/* حالت متن تیره */
.text-mode-dark {
  color: #1a1a1a;
}
.text-mode-dark .card-number {
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8);
}
.text-mode-dark .card-footer {
  background: rgba(255, 255, 255, 0.45);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}
.text-mode-dark .info-tag .label {
  background: rgba(0, 0, 0, 0.08);
}
.text-mode-dark .copy-btn {
  background: rgba(0, 0, 0, 0.08);
}

/* --- تنظیمات اختصاصی در صورت داشتن عکس پس‌زمینه --- */
.is-image-bg .card-footer {
  background: rgba(0, 0, 0, 0.42) !important;
  backdrop-filter: blur(4px) !important;
  -webkit-backdrop-filter: blur(4px) !important;
  border-top: none !important;
  padding-top: 4px !important;
  padding-bottom: 4px !important;
}

.is-image-bg.text-mode-dark .card-footer {
  background: rgba(255, 255, 255, 0.55) !important;
}

.is-image-bg .copy-btn {
  width: 22px !important;
  height: 22px !important;
}

/* المان‌های گرافیکی */
.card-glass-glow {
  position: absolute;
  top: -40px;
  left: -40px;
  width: 140px;
  height: 140px;
  background: rgba(255, 255, 255, 0.15);
  filter: blur(25px);
  border-radius: 50%;
  pointer-events: none;
}

.emv-chip {
  width: 36px;
  height: 26px;
  background: linear-gradient(135deg, #ffe066 0%, #d4af37 100%);
  border-radius: 5px;
  position: relative;
  box-shadow: inset 0 0 2px rgba(0, 0, 0, 0.4);
}

.chip-line {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  background: rgba(0, 0, 0, 0.3);
}

.card-description-bar {
  background: rgba(0, 0, 0, 0.5);
  border-top: 1px dashed rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.9);
}

.ltr-text {
  direction: ltr;
  text-align: left;
}
.dirltr {
  direction: ltr !important;
}
.cursor-pointer {
  cursor: pointer;
}
</style>
